import { useMemo } from "react"

import { Permission, Workspace, Connection } from "@/types"
import { useStore } from "@/store"
import { hasPermission } from "@/lib/utils"

export interface UsePermissionCheckOptions {
  permission: Permission
  workspace?: Workspace
  connection?: Connection
}

export interface UsePermissionCheckResult {
  hasPermission: boolean
  isLoading: boolean
}

/**
 * Hook to check if the current user has a specific permission.
 * Combines permissions from organization, workspace, and connection.
 */
export const usePermissionCheck = ({
  permission,
  workspace,
  connection,
}: UsePermissionCheckOptions): UsePermissionCheckResult => {
  const [currentOrganization] = useStore((state) => [state.currentOrganization])

  return useMemo(() => {
    if (!currentOrganization) {
      return { hasPermission: false, isLoading: true }
    }

    const userPermissions = new Set([
      ...currentOrganization.myPermissions,
      ...(workspace?.myPermissions || new Set()),
      ...(connection?.myPermissions || new Set()),
    ])

    return {
      hasPermission: hasPermission(userPermissions, permission),
      isLoading: false,
    }
  }, [currentOrganization, workspace, connection, permission])
}

export default usePermissionCheck
